import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkMarkService } from '@/services/trades/bulk-mark-service';
import { useToastStore } from '@/stores/toast-store';
import { describePoolPostings } from '@/utils/trades/cash-pool-display';
import { invalidateTradeAndPoolCaches } from './invalidate-trade-caches';
import type { BulkMarkRequest, BulkMarkResponse } from '@/types';

export const useBulkMark = () => {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<BulkMarkResponse, Error, BulkMarkRequest>({
    mutationFn: (request) => bulkMarkService.bulkMark(request),
    onSuccess: (resp) => {
      const marked = `Marked ${resp.marked} ${resp.marked === 1 ? 'position' : 'positions'}`;
      // A fee trade's mark restates its pool holding, which the dialog's Delta does not show.
      const moved = describePoolPostings(resp.pool_postings);
      addToast('success', moved ? `${marked} — ${moved}` : marked);
      invalidateTradeAndPoolCaches(queryClient);
      // Store cleanup is owned by the caller (MtmPage clears only the SUBMITTED rows'
      // overrides) so a bucket-filtered run can't discard overrides in a hidden bucket.
    },
    // NOTE: do NOT toast/invalidate on error — all-or-nothing means nothing was written.
    // The 400 failed[] body is parsed by the dialog (Plan 06-02) from mutation.error; see 06-CONTEXT.md Area 4.
  });
};
